// ตารางเวร pending counters — the shared store behind the sidebar badge and the
// two tab pills inside ตารางเวร › ทันตแพทย์.
//
// ONE store, module-level, exactly like ./notice.js and ./chat.js. A sidebar
// badge showing 8 over a tab row showing 6 is the specific failure that makes
// people stop looking, so there is only one copy of the numbers and every
// consumer reads it.
//
// Backend: GET /api/hr/schedule-pending-count → Xano api 655, which counts in the
// database and returns three integers. It takes no input and resolves the caller
// from $auth — there is nothing here to scope wrongly.
//
// 🔑 WHY A COUNT ENDPOINT RATHER THAN THE LISTS. DoctorScheduleLayout used to
// read its pills from GET /api/hr/doctor-leave + GET /api/hr/shift-request: two
// full enriched lists, fetched to render two numbers. Moving that into the
// sidebar — mounted on every page, refetching on focus — is how the missed-call
// badge served the whole app Cloudflare Error 1102 on 2026-08-24. See the header
// of functions/api/hr/schedule-pending-count.js.
import { useEffect, useState } from 'react'
import { apiGet } from './api.js'

const EMPTY = { leave: 0, shift: 0, total: 0 }

let _counts = EMPTY
let _loaded = false
const _subs = new Set()
const _emit = () => { for (const fn of _subs) fn() }

const n = (v) => Number(v) || 0

export function setSchedulePending(next) {
  if (!next || typeof next !== 'object') return
  const leave = n(next.leave)
  const shift = n(next.shift)
  _counts = { leave, shift, total: n(next.total) || leave + shift }
  _loaded = true
  _emit()
}

let _inflight = null

/**
 * Refresh the counters.
 *
 * 🔑 A FAILED POLL KEEPS THE PREVIOUS NUMBERS. "We don't know" must never render
 * as "nothing waiting" — a manager shown a cleared badge stops checking, and a
 * doctor's unanswered leave request is exactly the thing that must not go quiet.
 * Same rule ./notice.js and ./chat.js#refreshUnread both keep. It is also why the
 * proxy 502s on a malformed upstream payload instead of coercing it to zeros.
 *
 * `fresh` bypasses the proxy's 2-minute shared cache. Pass it the moment someone
 * ACTS — approves a leave, picks a doctor for a shift — because a badge that
 * lingers after you have dealt with the thing teaches people the badge is lying.
 * Never pass it from a poll or a focus handler: that is what the cache is for.
 *
 * Concurrent calls are collapsed: mount and window-focus fire together on a tab
 * switch, and that must be one request, not two.
 */
export async function refreshSchedulePending({ fresh = false } = {}) {
  if (_inflight) return _inflight
  _inflight = apiGet('/api/hr/schedule-pending-count', fresh ? { nocache: 1 } : undefined)
    .then((r) => { setSchedulePending(r); return _counts })
    .catch(() => _counts)
    .finally(() => { _inflight = null })
  return _inflight
}

/**
 * The self-heal timer, jittered.
 *
 * ⚠️ JITTER IS NOT DECORATION — a fixed interval re-synchronises every open tab
 * onto the same tick, which is a thundering herd with no spread at all. The
 * missed-call badge's flat 600ms debounce is what made one incoming call fetch in
 * every tab in the clinic at the same instant. Keep the jitter.
 *
 * Five minutes, not sixty seconds: nothing being counted here changes more than a
 * few times a week, and window focus already covers a manager coming back to the
 * tab. The timer only exists so a manager who leaves the CRM focused all day
 * still learns that a request arrived.
 */
const POLL_MS = 300000
const POLL_JITTER_MS = 60000
export const schedulePollDelay = () => POLL_MS + Math.floor(Math.random() * POLL_JITTER_MS)

/**
 * Subscribe to the counters.
 *
 * `autoLoad` fetches on mount, refreshes on window focus and runs the self-heal
 * timer. Pass it from ONE place only — the sidebar, which is always mounted — or
 * every badge on the page kicks its own first request. That is the mistake
 * ./chat.js documents and ./notice.js repeats the warning for.
 *
 * `enabled` is false for anyone with no ตารางเวร page (a Counter, a Lab
 * technician, an agency account), so the fetch does not 403 against the
 * middleware's /api/hr/* gate for a badge they will never be shown.
 */
export function useSchedulePending({ autoLoad = false, enabled = true } = {}) {
  const [, force] = useState(0)
  useEffect(() => {
    const rerender = () => force((x) => x + 1)
    _subs.add(rerender)
    if (!autoLoad || !enabled) return () => { _subs.delete(rerender) }

    let alive = true
    let timer = null
    const tick = async () => {
      await refreshSchedulePending()
      if (alive) timer = setTimeout(tick, schedulePollDelay())
    }
    tick()
    const onFocus = () => refreshSchedulePending()
    window.addEventListener('focus', onFocus)
    return () => {
      alive = false
      clearTimeout(timer)
      _subs.delete(rerender)
      window.removeEventListener('focus', onFocus)
    }
  }, [autoLoad, enabled])
  return _counts
}

// Has anything ever loaded? Lets a badge stay ABSENT (rather than render a
// confident 0) until the first answer arrives — a blank badge and a broken badge
// must not look identical.
export const schedulePendingLoaded = () => _loaded
